import { useState, useEffect } from 'react';
import { useUser } from '@/contexts/UserContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Shield, Server, AlertCircle, CheckCircle2, Clock, Loader2, Info } from 'lucide-react';

interface VerifactuConfig {
  userId: string;
  modalidad: 'VERIFACTU' | 'NO_VERIFACTU';
  entornoAEAT: 'pruebas' | 'produccion';
  politicaEnvio: 'manual' | 'automatico' | 'programado';
  envioAutomaticoHabilitado: boolean;
  horaEnvioProgramado?: string;
  ultimoEnvioAEAT?: string;
  createdAt: string;
  updatedAt: string;
}

interface ComplianceStatus {
  modalidadConfigurada: boolean;
  certificadoValido: boolean;
  cadenaIntegridadValida: boolean;
  ultimoEnvioExitoso?: string;
  facturasPendientesEnvio: number;
  alertas: Array<{
    tipo: 'error' | 'warning' | 'info';
    mensaje: string;
    timestamp: string;
  }>;
}

export function VerifactuConfig() {
  const { fetchWithAuth } = useUser();
  const [config, setConfig] = useState<VerifactuConfig | null>(null);
  const [status, setStatus] = useState<ComplianceStatus | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [configRes, statusRes] = await Promise.all([
        fetchWithAuth('/verifactu/config'),
        fetchWithAuth('/verifactu/status'),
      ]);

      if (configRes.ok) {
        const configData = await configRes.json();
        setConfig(configData);
      }

      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setStatus(statusData);
      }
    } catch (error) {
      toast.error('Error al cargar configuración Verifactu', {
        description: error instanceof Error ? error.message : 'Error desconocido',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!config) return;

    setIsSaving(true);
    try {
      const res = await fetchWithAuth('/verifactu/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });

      if (!res.ok) {
        throw new Error('Error al guardar configuración');
      }

      toast.success('Configuración guardada', {
        description: 'La configuración de Verifactu ha sido actualizada correctamente',
      });

      // Recargar estado
      await loadData();
    } catch (error) {
      toast.error('Error al guardar configuración', {
        description: error instanceof Error ? error.message : 'Error desconocido',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateConfig = (updates: Partial<VerifactuConfig>) => {
    setConfig((prev) => (prev ? { ...prev, ...updates } : null));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!config) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>No se pudo cargar la configuración de Verifactu</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Estado de Cumplimiento */}
      {status && (
        <Card className={status.alertas.some((a) => a.tipo === 'error') ? 'border-red-200' : 'border-green-200'}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {status.certificadoValido && status.cadenaIntegridadValida ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              Estado de Cumplimiento Verifactu
            </CardTitle>
            <CardDescription>
              Verifica que tu sistema cumple con la normativa de facturación electrónica
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
                <span className="text-sm font-medium">Modalidad configurada</span>
                {status.modalidadConfigurada ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600" />
                )}
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
                <span className="text-sm font-medium">Certificado válido</span>
                {status.certificadoValido ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600" />
                )}
              </div>
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded">
                <span className="text-sm font-medium">Cadena de integridad</span>
                {status.cadenaIntegridadValida ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600" />
                )}
              </div>
            </div>

            {status.facturasPendientesEnvio > 0 && (
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription>
                  Tienes <strong>{status.facturasPendientesEnvio}</strong> facturas pendientes de envío a la AEAT
                </AlertDescription>
              </Alert>
            )}

            {status.alertas.length > 0 && (
              <div className="space-y-2">
                {status.alertas.map((alerta, idx) => (
                  <Alert
                    key={idx}
                    variant={alerta.tipo === 'error' ? 'destructive' : 'default'}
                    className={alerta.tipo === 'warning' ? 'border-yellow-200 bg-yellow-50' : ''}
                  >
                    <AlertDescription>{alerta.mensaje}</AlertDescription>
                  </Alert>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Configuración de Modalidad */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Modalidad de Cumplimiento
          </CardTitle>
          <CardDescription>
            Selecciona cómo deseas cumplir con la normativa Verifactu
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Modalidad</Label>
            <Select
              value={config.modalidad}
              onValueChange={(value: 'VERIFACTU' | 'NO_VERIFACTU') => updateConfig({ modalidad: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="VERIFACTU">
                  <div className="flex flex-col items-start">
                    <span className="font-semibold">VERI*FACTU</span>
                    <span className="text-xs text-muted-foreground">
                      Envío online en tiempo real a la AEAT
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="NO_VERIFACTU">
                  <div className="flex flex-col items-start">
                    <span className="font-semibold">NO VERI*FACTU</span>
                    <span className="text-xs text-muted-foreground">
                      Conservación local con log de eventos firmado
                    </span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {config.modalidad === 'VERIFACTU'
                ? 'Los registros se enviarán automáticamente a la AEAT. No es necesario conservarlos localmente.'
                : 'Los registros se conservarán localmente. Debes mantener un log de eventos de sistema firmado digitalmente.'}
            </p>
          </div>

          {config.modalidad === 'VERIFACTU' && (
            <>
              <div className="space-y-2">
                <Label>Entorno de la AEAT</Label>
                <Select
                  value={config.entornoAEAT}
                  onValueChange={(value: 'pruebas' | 'produccion') => updateConfig({ entornoAEAT: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pruebas">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">Pruebas</Badge>
                        <span>Entorno de desarrollo</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="produccion">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-600">Producción</Badge>
                        <span>Entorno real de la AEAT</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {config.entornoAEAT === 'pruebas'
                    ? '⚠️ Modo de pruebas. Los registros NO son válidos fiscalmente.'
                    : '✓ Modo de producción. Los registros son oficiales y válidos.'}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Política de Envío</Label>
                <Select
                  value={config.politicaEnvio}
                  onValueChange={(value: 'manual' | 'automatico' | 'programado') =>
                    updateConfig({ politicaEnvio: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual - Envío bajo demanda</SelectItem>
                    <SelectItem value="automatico">Automático - Envío inmediato al crear factura</SelectItem>
                    <SelectItem value="programado">Programado - Envío en horario específico</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {config.politicaEnvio === 'programado' && (
                <div className="space-y-2">
                  <Label>Hora de Envío Programado</Label>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <Input
                      type="time"
                      value={config.horaEnvioProgramado || '23:00'}
                      onChange={(e) => updateConfig({ horaEnvioProgramado: e.target.value })}
                      className="max-w-[200px]"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Las facturas se enviarán automáticamente cada día a esta hora
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between p-4 bg-muted/50 rounded">
                <div>
                  <p className="font-medium">Envío Automático</p>
                  <p className="text-sm text-muted-foreground">
                    {config.politicaEnvio === 'automatico'
                      ? 'Las facturas se envían automáticamente al crearlas'
                      : 'Desactivado - Envío manual o programado'}
                  </p>
                </div>
                <Switch
                  checked={config.envioAutomaticoHabilitado}
                  onCheckedChange={(checked) => updateConfig({ envioAutomaticoHabilitado: checked })}
                  disabled={config.politicaEnvio !== 'automatico'}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Información del Sistema */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Información del Sistema Informático
          </CardTitle>
          <CardDescription>
            Datos del desarrollador del software (según Art. 15 Orden HAC/1177/2024)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              Estos datos identifican al <strong>productor del software</strong> (desarrollador) y no pueden ser modificados por el usuario.
              Son obligatorios para el cumplimiento de la normativa VeriFactu.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Productor del Sistema</Label>
              <div className="p-3 bg-muted/50 rounded border">
                <p className="font-medium">Raúl Alonso Lorente</p>
                <p className="text-sm text-muted-foreground">NIF: 30821952C</p>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-muted-foreground">Sistema Informático</Label>
              <div className="p-3 bg-muted/50 rounded border">
                <p className="font-medium">FacturaPro2</p>
                <p className="text-sm text-muted-foreground">ID: FP • Versión: 2.0.0</p>
              </div>
            </div>
          </div>

          {config.ultimoEnvioAEAT && (
            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950/30 rounded border border-green-200 dark:border-green-800">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <div>
                <p className="text-sm font-medium">Último envío exitoso</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(config.ultimoEnvioAEAT).toLocaleString('es-ES', {
                    dateStyle: 'long',
                    timeStyle: 'short',
                  })}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Botones de acción */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={loadData}>
          Recargar
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : (
            'Guardar Configuración'
          )}
        </Button>
      </div>
    </div>
  );
}
